import { z } from "zod"

import { marketplaceIdSchema } from "../marketplace-id"
import {
  adAnalysisDecisionSchema,
  adAnalysisGateResultSchema,
  adAnalysisPrioritySchema,
  adProfitabilityActionSchema,
} from "./economics"
import { isoCalendarDay } from "./primitives"

export const CAMPAIGN_ACTION_KIND = {
  create: "create",
  setBid: "set-bid",
  activate: "activate",
  deactivate: "deactivate",
} as const
export const campaignActionKindSchema = z.enum(CAMPAIGN_ACTION_KIND)
export type CampaignActionKind = z.infer<typeof campaignActionKindSchema>

export const NEW_CAMPAIGN_INITIAL_BID_CENTS = 35
export const NEW_CAMPAIGN_DAILY_BUDGET_CENTS = 2_000
export const MAX_CAMPAIGN_ACTION_BID_CENTS = 2_147_483_647

export const CAMPAIGN_ACTION_PLAN_STATUS = {
  staging: "staging",
  awaitingApproval: "awaiting_approval",
  approved: "approved",
  executing: "executing",
  appliedAndVerified: "applied_and_verified",
  alreadyApplied: "already_applied",
  failedBeforeClick: "failed_before_click",
  uncertainAfterPossibleClick: "uncertain_after_possible_click",
  simulated: "simulated",
} as const
export const campaignActionPlanStatusSchema = z.enum(CAMPAIGN_ACTION_PLAN_STATUS)
export type CampaignActionPlanStatus = z.infer<typeof campaignActionPlanStatusSchema>

export const TERMINAL_CAMPAIGN_ACTION_PLAN_STATUSES = new Set<CampaignActionPlanStatus>([
  CAMPAIGN_ACTION_PLAN_STATUS.appliedAndVerified,
  CAMPAIGN_ACTION_PLAN_STATUS.alreadyApplied,
  CAMPAIGN_ACTION_PLAN_STATUS.failedBeforeClick,
  CAMPAIGN_ACTION_PLAN_STATUS.uncertainAfterPossibleClick,
  CAMPAIGN_ACTION_PLAN_STATUS.simulated,
])

export const campaignActionExecutionTerminalStatusSchema = z.enum([
  CAMPAIGN_ACTION_PLAN_STATUS.appliedAndVerified,
  CAMPAIGN_ACTION_PLAN_STATUS.alreadyApplied,
  CAMPAIGN_ACTION_PLAN_STATUS.failedBeforeClick,
  CAMPAIGN_ACTION_PLAN_STATUS.uncertainAfterPossibleClick,
])
export type CampaignActionExecutionTerminalStatus = z.infer<
  typeof campaignActionExecutionTerminalStatusSchema
>

export const campaignStateSnapshotSchema = z
  .object({
    campaignId: z.string().min(1).nullable(),
    redcareProductId: z.string().min(1),
    productName: z.string().min(1),
    campaignName: z.string().min(1),
    startDate: isoCalendarDay,
    endDate: isoCalendarDay,
    bidCents: z.number().int().positive(),
    dailyBudgetCents: z.number().int().nonnegative(),
    isActive: z.boolean(),
    soldOut: z.boolean(),
    offerUsable: z.boolean(),
  })
  .strict()
export type CampaignStateSnapshot = z.infer<typeof campaignStateSnapshotSchema>

export const campaignPlanActionSchema = z
  .object({
    actionId: z.string().min(1).max(80),
    kind: campaignActionKindSchema,
    label: z.string().min(1).max(200),
    recommended: z.boolean(),
    manualOverride: z.boolean(),
    dependsOnActionIds: z.array(z.string().min(1).max(80)).max(3),
    before: campaignStateSnapshotSchema.nullable(),
    after: campaignStateSnapshotSchema,
    changedFields: z.array(z.string().min(1)).min(1).max(20),
    preservedFields: z.array(z.string().min(1)).max(20),
  })
  .strict()
export type CampaignPlanAction = z.infer<typeof campaignPlanActionSchema>

export const campaignPlanSubjectSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("existing-campaign"),
      campaignId: z.string().min(1),
      productId: z.string().min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal("new-campaign-candidate"),
      productId: z.string().min(1),
    })
    .strict(),
])
export type CampaignPlanSubject = z.infer<typeof campaignPlanSubjectSchema>

export const campaignPlanIntentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ai-recommendation") }).strict(),
  z.object({ kind: z.literal("manual-opposite") }).strict(),
  z
    .object({
      kind: z.literal("manual-bid"),
      bidCents: z.number().int().positive().max(MAX_CAMPAIGN_ACTION_BID_CENTS),
    })
    .strict(),
])
export type CampaignPlanIntent = z.infer<typeof campaignPlanIntentSchema>

export const campaignActionStageBodySchema = z
  .object({
    subject: campaignPlanSubjectSchema,
    intent: campaignPlanIntentSchema,
    endDate: isoCalendarDay.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    const candidate = value.subject.kind === "new-campaign-candidate"
    if (!candidate && value.endDate !== undefined) {
      context.addIssue({
        code: "custom",
        message: "endDate is only editable for a new campaign",
      })
    }
    if (candidate && value.intent.kind === "manual-bid") {
      context.addIssue({
        code: "custom",
        message: "manual bid changes require an existing campaign",
      })
    }
  })
export type CampaignActionStageBody = z.infer<typeof campaignActionStageBodySchema>

export const campaignActionApprovalBodySchema = z
  .object({
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
    selectedActionIds: z.array(z.string().min(1).max(80)).min(1).max(4),
    manualOverrideReason: z.string().trim().min(4).max(500).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (new Set(value.selectedActionIds).size !== value.selectedActionIds.length) {
      context.addIssue({ code: "custom", message: "selectedActionIds must be unique" })
    }
  })
export type CampaignActionApprovalBody = z.infer<typeof campaignActionApprovalBodySchema>

export const campaignActionPlanStagingBodySchema = z
  .object({
    portalProductId: z.string().trim().min(1).max(200),
    actions: z.array(campaignPlanActionSchema).min(1).max(4),
    liveReadAt: z.string().datetime({ offset: true }),
    expiresAt: z.string().datetime({ offset: true }),
    safetyState: z.enum(["eligible", "blocked", "duplicate-linked"]),
    safetyReason: z.string().trim().min(1).max(1000).nullable(),
    duplicateCampaignId: z.string().min(1).nullable(),
    evidenceRef: z.string().trim().min(1).max(500).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const ids = value.actions.map((action) => action.actionId)
    if (new Set(ids).size !== ids.length) {
      context.addIssue({ code: "custom", message: "actionIds must be unique" })
    }
    if (value.safetyState === "duplicate-linked" && value.duplicateCampaignId === null) {
      context.addIssue({
        code: "custom",
        message: "duplicateCampaignId is required for duplicate-linked",
      })
    }
  })
export type CampaignActionPlanStagingBody = z.infer<typeof campaignActionPlanStagingBodySchema>

export const campaignActionCapabilitySchema = z
  .object({
    marketplace: marketplaceIdSchema,
    enabled: z.boolean(),
    generation: z.number().int().nonnegative(),
    updatedBy: z.string().min(1),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict()
export type CampaignActionCapability = z.infer<typeof campaignActionCapabilitySchema>

export const updateCampaignActionCapabilityBodySchema = z
  .object({
    enabled: z.boolean(),
    confirmation: z.literal("set-campaign-action-capability"),
  })
  .strict()
export type UpdateCampaignActionCapabilityBody = z.infer<
  typeof updateCampaignActionCapabilityBodySchema
>

export const campaignActionPlanSchema = z
  .object({
    id: z.string().uuid(),
    marketplace: marketplaceIdSchema,
    subject: campaignPlanSubjectSchema,
    portalProductId: z.string().min(1).nullable(),
    intent: campaignPlanIntentSchema,
    analysisRunId: z.string().uuid(),
    analysisRowPosition: z.number().int().nonnegative(),
    requestedStartDate: isoCalendarDay.nullable(),
    requestedEndDate: isoCalendarDay.nullable(),
    requestedBidCents: z.number().int().positive().nullable(),
    status: campaignActionPlanStatusSchema,
    actions: z.array(campaignPlanActionSchema).max(4),
    stagingFingerprint: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .nullable(),
    capabilityGeneration: z.number().int().positive().nullable(),
    fingerprint: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .nullable(),
    selectedActionIds: z.array(z.string()),
    liveReadAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    safetyState: z.enum(["pending", "eligible", "blocked", "duplicate-linked"]),
    safetyReason: z.string().nullable(),
    duplicateCampaignId: z.string().nullable(),
    approvedByUserId: z.number().int().positive().nullable(),
    approvedBy: z.string().nullable(),
    approvedAt: z.string().nullable(),
    manualOverrideReason: z.string().nullable(),
    resultReason: z.string().nullable(),
    resultAfter: campaignStateSnapshotSchema.nullable(),
    evidenceRef: z.string().nullable(),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict()
export type CampaignActionPlan = z.infer<typeof campaignActionPlanSchema>

export const campaignActionAnalysisIdentitySchema = z
  .object({
    runId: z.string().uuid(),
    rowPosition: z.number().int().nonnegative(),
    completedAt: z.string().datetime({ offset: true }),
  })
  .strict()
export type CampaignActionAnalysisIdentity = z.infer<typeof campaignActionAnalysisIdentitySchema>

export const campaignActionAnalysisDetailSchema = z
  .object({
    runId: z.string().uuid(),
    rowPosition: z.number().int().nonnegative(),
    productId: z.string().min(1),
    productName: z.string().nullable(),
    ean: z.string().nullable(),
    reportSource: z.string().min(1),
    campaignId: z.string(),
    analysisDate: isoCalendarDay,
    completedAt: z.string().datetime({ offset: true }),
    decision: adAnalysisDecisionSchema,
    priority: adAnalysisPrioritySchema,
    recommendedBid: z.number().nullable(),
    reasoning: z.string(),
    nextStep: z.string(),
    sourceUrl: z.string().nullable(),
    currentBid: z.number().nullable(),
    clicks: z.number().nullable(),
    conversions: z.number().nullable(),
    spend: z.number().nullable(),
    gmv: z.number().nullable(),
    roas: z.number().nullable(),
    breakEvenRoas: z.number().nullable(),
    targetRoas: z.number().nullable(),
    applicableBidCeiling: z.number().nullable(),
    engineAction: adProfitabilityActionSchema.nullable(),
    engineBid: z.number().nullable(),
    gateResult: adAnalysisGateResultSchema,
    gateReasons: z.array(z.string()),
    organicUnits: z.number().nullable(),
    organicRevenue: z.number().nullable(),
    organicOrders: z.number().int().nullable(),
    unitMargin: z.number().nullable(),
    marginPool: z.number().nullable(),
    isActive: z.boolean().nullable(),
    soldOut: z.boolean().nullable(),
  })
  .strict()
export type CampaignActionAnalysisDetail = z.infer<typeof campaignActionAnalysisDetailSchema>

export const campaignActionPlanListResponseSchema = z
  .object({
    plans: z.array(campaignActionPlanSchema),
    latestAnalysis: campaignActionAnalysisIdentitySchema.nullable(),
    latestAnalysisDetail: campaignActionAnalysisDetailSchema.nullable(),
  })
  .strict()
export type CampaignActionPlanListResponse = z.infer<typeof campaignActionPlanListResponseSchema>

export const campaignActionPlanListQuerySchema = z
  .object({
    subjectKind: z.enum(["existing-campaign", "new-campaign-candidate"]),
    productId: z.string().min(1),
    campaignId: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.subjectKind === "existing-campaign" && value.campaignId === undefined) {
      context.addIssue({
        code: "custom",
        message: "campaignId is required for an existing campaign",
      })
    }
    if (value.subjectKind === "new-campaign-candidate" && value.campaignId !== undefined) {
      context.addIssue({ code: "custom", message: "campaignId is forbidden for a new candidate" })
    }
  })
export type CampaignActionPlanListQuery = z.infer<typeof campaignActionPlanListQuerySchema>

export const campaignApprovalEnvelopeSchema = z
  .object({
    version: z.literal(1),
    planId: z.string().uuid(),
    marketplace: marketplaceIdSchema,
    subject: campaignPlanSubjectSchema,
    portalProductId: z.string().min(1),
    intent: campaignPlanIntentSchema,
    analysis: z
      .object({ runId: z.string().uuid(), rowPosition: z.number().int().nonnegative() })
      .strict(),
    requestedDates: z
      .object({ start: isoCalendarDay.nullable(), end: isoCalendarDay.nullable() })
      .strict(),
    requestedBidCents: z.number().int().positive().nullable(),
    capabilityGeneration: z.number().int().positive(),
    executionFingerprint: z.string().regex(/^[0-9a-f]{64}$/),
    actions: z.array(campaignPlanActionSchema).min(1).max(4),
    selectedActionIds: z.array(z.string().min(1).max(80)).min(1).max(4),
    manualOverrideReason: z.string().nullable(),
    approvedBy: z
      .object({ userId: z.number().int().positive(), subject: z.string().min(1) })
      .strict(),
  })
  .strict()
export type CampaignApprovalEnvelope = z.infer<typeof campaignApprovalEnvelopeSchema>

export const campaignActionPlanClaimBodySchema = z
  .object({
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
    recover: z.boolean(),
  })
  .strict()
export type CampaignActionPlanClaimBody = z.infer<typeof campaignActionPlanClaimBodySchema>

export const campaignActionPlanClaimResponseSchema = z
  .object({
    plan: campaignActionPlanSchema,
    approval: campaignApprovalEnvelopeSchema,
    executionToken: z.string().uuid(),
    leaseExpiresAt: z.string().datetime({ offset: true }),
  })
  .strict()
export type CampaignActionPlanClaimResponse = z.infer<typeof campaignActionPlanClaimResponseSchema>

export const campaignActionPlanHeartbeatBodySchema = z
  .object({ executionToken: z.string().uuid() })
  .strict()
export type CampaignActionPlanHeartbeatBody = z.infer<typeof campaignActionPlanHeartbeatBodySchema>

export const campaignActionPlanLeaseResponseSchema = z
  .object({
    planId: z.string().uuid(),
    status: z.literal(CAMPAIGN_ACTION_PLAN_STATUS.executing),
    leaseExpiresAt: z.string().datetime({ offset: true }),
  })
  .strict()
export type CampaignActionPlanLeaseResponse = z.infer<typeof campaignActionPlanLeaseResponseSchema>

export const campaignActionPlanActionPermitBodySchema = z
  .object({
    executionToken: z.string().uuid(),
    snapshot: campaignStateSnapshotSchema,
    snapshotReadAt: z.string().datetime({ offset: true }),
    marketplaceDate: isoCalendarDay,
  })
  .strict()
export type CampaignActionPlanActionPermitBody = z.infer<
  typeof campaignActionPlanActionPermitBodySchema
>

export const campaignActionPlanActionPermitResponseSchema = z
  .object({
    planId: z.string().uuid(),
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
    action: campaignPlanActionSchema,
    snapshot: campaignStateSnapshotSchema,
    marketplaceDate: isoCalendarDay,
    permitExpiresAt: z.string().datetime({ offset: true }),
  })
  .strict()
export type CampaignActionPlanActionPermitResponse = z.infer<
  typeof campaignActionPlanActionPermitResponseSchema
>

export const campaignActionPlanResultBodySchema = z
  .object({
    executionToken: z.string().uuid(),
    status: campaignActionExecutionTerminalStatusSchema,
    reason: z.string().trim().min(1).max(1_000),
    after: campaignStateSnapshotSchema.nullable(),
    evidenceRef: z.string().trim().min(1).max(500).nullable(),
  })
  .strict()
export type CampaignActionPlanResultBody = z.infer<typeof campaignActionPlanResultBodySchema>

export function campaignApprovalEnvelope(input: {
  plan: Pick<
    CampaignActionPlan,
    | "id"
    | "marketplace"
    | "subject"
    | "portalProductId"
    | "intent"
    | "analysisRunId"
    | "analysisRowPosition"
    | "requestedStartDate"
    | "requestedEndDate"
    | "requestedBidCents"
    | "capabilityGeneration"
    | "stagingFingerprint"
    | "actions"
  >
  selectedActionIds: string[]
  manualOverrideReason: string | null
  approvedByUserId: number
  approvedBy: string
}): CampaignApprovalEnvelope {
  const executionFingerprint = input.plan.stagingFingerprint
  if (executionFingerprint === null) {
    throw new Error("staged campaign action plan has no execution fingerprint")
  }
  if (input.plan.portalProductId === null) {
    throw new Error("staged campaign action plan has no portal product identity")
  }
  if (input.plan.capabilityGeneration === null) {
    throw new Error("staged campaign action plan has no capability generation")
  }
  const selected = new Set(input.selectedActionIds)
  return {
    version: 1,
    planId: input.plan.id,
    marketplace: input.plan.marketplace,
    subject: input.plan.subject,
    portalProductId: input.plan.portalProductId,
    intent: input.plan.intent,
    analysis: { runId: input.plan.analysisRunId, rowPosition: input.plan.analysisRowPosition },
    requestedDates: {
      start: input.plan.requestedStartDate,
      end: input.plan.requestedEndDate,
    },
    requestedBidCents: input.plan.requestedBidCents,
    capabilityGeneration: input.plan.capabilityGeneration,
    executionFingerprint,
    actions: input.plan.actions.filter((action) => selected.has(action.actionId)),
    selectedActionIds: input.selectedActionIds,
    manualOverrideReason: input.manualOverrideReason,
    approvedBy: { userId: input.approvedByUserId, subject: input.approvedBy },
  }
}

/** Add calendar months without overflowing into the following month.
 * `2026-08-31 + 3` therefore becomes `2026-11-30`. */
export function addCalendarMonthsClamped(day: string, months: number): string {
  const parsed = new Date(`${day}T00:00:00.000Z`)
  if (Number.isNaN(parsed.valueOf()) || parsed.toISOString().slice(0, 10) !== day) {
    throw new Error(`Invalid ISO calendar day: ${day}`)
  }
  const targetMonth = parsed.getUTCMonth() + months
  const first = new Date(Date.UTC(parsed.getUTCFullYear(), targetMonth, 1))
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0))
  const result = new Date(
    Date.UTC(
      first.getUTCFullYear(),
      first.getUTCMonth(),
      Math.min(parsed.getUTCDate(), last.getUTCDate())
    )
  )
  return result.toISOString().slice(0, 10)
}

/** The marketplace-local calendar day for an instant. Paid candidate campaigns
 * always start on this day; callers may edit only the end date. */
export function marketplaceCalendarDay(now: Date, timeZone: string): string {
  const local = localDateParts(now, timeZone)
  return `${local.year}-${pad(local.month)}-${pad(local.day)}`
}

export function defaultNewCampaignDates(
  timeZone: string,
  now: Date = new Date()
): { startDate: string; endDate: string } {
  const startDate = marketplaceCalendarDay(now, timeZone)
  return { startDate, endDate: addCalendarMonthsClamped(startDate, 3) }
}

/** End of the completed analysis's marketplace-local calendar week. */
export function campaignRecommendationExpiresAt(completedAt: string, timeZone: string): string {
  const local = localDateParts(new Date(completedAt), timeZone)
  const weekday = new Date(Date.UTC(local.year, local.month - 1, local.day)).getUTCDay()
  const daysThroughSunday = (7 - weekday) % 7
  const monday = new Date(Date.UTC(local.year, local.month - 1, local.day + daysThroughSunday + 1))
  const mondayUtc = localMidnightToUtc(
    { year: monday.getUTCFullYear(), month: monday.getUTCMonth() + 1, day: monday.getUTCDate() },
    timeZone
  )
  return new Date(mondayUtc.getTime() - 1).toISOString()
}

export function nextMarketplaceMidnight(now: Date, timeZone: string): string {
  const local = localDateParts(now, timeZone)
  const tomorrow = new Date(Date.UTC(local.year, local.month - 1, local.day + 1))
  return localMidnightToUtc(
    {
      year: tomorrow.getUTCFullYear(),
      month: tomorrow.getUTCMonth() + 1,
      day: tomorrow.getUTCDate(),
    },
    timeZone
  ).toISOString()
}

function localDateParts(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    calendar: "iso8601",
    numberingSystem: "latn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant)
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0")
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
  }
}

function localMidnightToUtc(
  date: { year: number; month: number; day: number },
  timeZone: string
): Date {
  const target = Date.UTC(date.year, date.month - 1, date.day)
  let guess = new Date(target)
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = localDateParts(guess, timeZone)
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second
    )
    guess = new Date(guess.getTime() + target - actualAsUtc)
  }
  return guess
}

function pad(value: number): string {
  return String(value).padStart(2, "0")
}
